import { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

interface SpreadsheetPreviewProps {
  fileUrl: string;
  fileName: string;
}

interface SheetData {
  name: string;
  data: any[][];
}

export const SpreadsheetPreview = ({ fileUrl, fileName }: SpreadsheetPreviewProps) => {
  const [sheets, setSheets] = useState<SheetData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSpreadsheet = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(fileUrl);
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array", cellDates: true, cellNF: true });

        const sheetsData: SheetData[] = workbook.SheetNames.map((sheetName) => {
          const worksheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false, defval: "" }) as any[][];
          return { name: sheetName, data };
        });

        setSheets(sheetsData);
      } catch (err: any) {
        console.error("Failed to load spreadsheet:", err);
        setError("Failed to load spreadsheet. Please try downloading the file instead.");
      } finally {
        setLoading(false);
      }
    };

    loadSpreadsheet();
  }, [fileUrl]);

  const formatCellValue = (value: any): string => {
    if (value === null || value === undefined || value === "") return "";
    
    // Handle dates
    if (value instanceof Date) {
      return value.toLocaleDateString();
    }
    
    // Handle numbers - preserve formatting
    if (typeof value === "number") {
      // Check if it's a currency (has 2 decimal places and is reasonably large)
      if (value % 1 !== 0 && value > 0.01) {
        return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      }
      return value.toLocaleString();
    }
    
    return String(value);
  };

  const isNumeric = (value: any): boolean => {
    if (typeof value === "number") return true;
    if (typeof value === "string") {
      const num = parseFloat(value.replace(/[^0-9.-]/g, ''));
      return !isNaN(num) && value.match(/\d/) !== null;
    }
    return false;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-destructive/10 text-destructive rounded-md">
        {error}
      </div>
    );
  }

  if (sheets.length === 0) {
    return (
      <div className="p-4 text-muted-foreground text-center">
        No data found in spreadsheet
      </div>
    );
  }

  const renderSheet = (sheet: SheetData) => {
    if (!sheet.data || sheet.data.length === 0) {
      return (
        <div className="p-4 text-muted-foreground text-center">
          No data in this sheet
        </div>
      );
    }

    const headers = sheet.data[0] || [];
    const rows = sheet.data.slice(1);

    return (
      <ScrollArea className="h-[70vh] w-full border rounded-md">
        <div className="min-w-full">
          <Table>
            <TableHeader className="sticky top-0 bg-background z-10 shadow-sm">
              <TableRow className="border-b-2">
                <TableHead className="w-12 font-semibold bg-muted/50 sticky left-0 z-20 border-r-2">#</TableHead>
                {headers.map((header: any, index: number) => (
                  <TableHead key={index} className="font-semibold min-w-[120px] bg-muted/50 border-r">
                    <div className="flex flex-col">
                      <span className="text-xs text-muted-foreground">{String.fromCharCode(65 + index)}</span>
                      <span>{header || `Column ${index + 1}`}</span>
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row: any[], rowIndex: number) => (
                <TableRow 
                  key={rowIndex} 
                  className={cn(
                    "hover:bg-muted/50 transition-colors",
                    rowIndex % 2 === 0 ? "bg-background" : "bg-muted/20"
                  )}
                >
                  <TableCell className="font-medium text-muted-foreground bg-muted/30 sticky left-0 z-10 border-r">
                    {rowIndex + 1}
                  </TableCell>
                  {headers.map((_, cellIndex: number) => {
                    const cellValue = row[cellIndex];
                    const isNumber = isNumeric(cellValue);
                    return (
                      <TableCell 
                        key={cellIndex} 
                        className={cn(
                          "max-w-xs py-2 px-3 border-r align-top",
                          isNumber ? "text-right font-mono" : "text-left"
                        )}
                        title={formatCellValue(cellValue)}
                      >
                        <div className="break-words whitespace-pre-wrap">
                          {formatCellValue(cellValue)}
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <ScrollBar orientation="horizontal" />
        <ScrollBar orientation="vertical" />
      </ScrollArea>
    );
  };

  if (sheets.length === 1) {
    return renderSheet(sheets[0]);
  }

  return (
    <Tabs defaultValue={sheets[0].name} className="w-full">
      <TabsList>
        {sheets.map((sheet) => (
          <TabsTrigger key={sheet.name} value={sheet.name}>
            {sheet.name}
          </TabsTrigger>
        ))}
      </TabsList>
      {sheets.map((sheet) => (
        <TabsContent key={sheet.name} value={sheet.name}>
          {renderSheet(sheet)}
        </TabsContent>
      ))}
    </Tabs>
  );
};
